import { Text, View } from "@react-pdf/renderer";

import { Attribution } from "~/common/components";
import { getPdfPatientInformationFromPatient } from "~/providers/components/pdf/utils";
import { formatSessionDateRange } from "~/providers/utils/formatSessionDateRange";
import {
  ProgressNotePdfExport,
  ReimbursementReportData,
} from "~/providers/utils/types/consultation";
import { PdfDocument, PdfMarkdown, PdfPatientInformationCard } from "../shared";
import { styles } from "../styles";

const INTAKE_HISTORY_SECTIONS: {
  key: keyof ReimbursementReportData;
  title: string;
}[] = [
  { key: "psychiatric_history", title: "Psychiatric history" },
  { key: "medical_history", title: "Medical history" },
  { key: "substance_history", title: "Substance history" },
  {
    key: "symptoms_progression_over_time",
    title: "Symptoms progression over time",
  },
  { key: "past_personal_history", title: "Past personal history" },
  { key: "educational_history", title: "Educational history" },
  { key: "family_history", title: "Family history" },
];

const sectionStyle = (isLastBeforeFinalNote: boolean) =>
  isLastBeforeFinalNote
    ? [styles.section, { borderBottomWidth: 0 }]
    : [styles.section];

const getLastSectionBeforeFinalNote = (
  data: ReimbursementReportData,
): string => {
  for (let index = INTAKE_HISTORY_SECTIONS.length - 1; index >= 0; index -= 1) {
    const { key } = INTAKE_HISTORY_SECTIONS[index];
    if (data[key]?.trim()) {
      return key;
    }
  }

  if (data.history_of_present_illness?.trim()) {
    return "history_of_present_illness";
  }

  return "allergies";
};

export const ReimbursementExportDocument = ({
  data,
}: {
  data: ProgressNotePdfExport;
}) => {
  const {
    patient,
    noteData,
    completedAt,
    completedBy,
    basicPatientInformation,
    actualStartTime,
    actualEndTime,
  } = data;
  const teamMember = completedBy?.team_member;
  const patientInformation = getPdfPatientInformationFromPatient(
    patient,
    basicPatientInformation,
  );
  const meetingTimeRange = actualStartTime
    ? formatSessionDateRange(actualStartTime, actualEndTime ?? null, null)
    : null;
  const lastSectionBeforeFinalNote = getLastSectionBeforeFinalNote(noteData);

  return (
    <PdfDocument
      documentTitle="Psychiatric consultation"
      headerTitle={`Psychiatric consultation for ${patientInformation.nameWithAge}`}
    >
      <View style={[styles.section, styles.tight]}>
        <Text style={[styles.h1, { color: "#2433A8", marginBottom: 4 }]}>
          Psychiatric consultation
        </Text>
        <Text>
          <Attribution
            firstName={teamMember?.name}
            lastName={teamMember?.last_name}
            date={completedAt ?? undefined}
          />
        </Text>
      </View>

      <PdfPatientInformationCard
        {...patientInformation}
        meetingTimeRange={meetingTimeRange}
      />

      <View style={[styles.section]} wrap={false}>
        <Text style={[styles.h2]}>Current medications</Text>
        <Text style={styles.upperfirst}>
          {noteData.medications?.trim() ? noteData.medications.trim() : "None"}
        </Text>
      </View>

      <View
        style={sectionStyle(lastSectionBeforeFinalNote === "allergies")}
        wrap={false}
      >
        <Text style={[styles.h2]}>Allergies</Text>
        <Text style={styles.upperfirst}>
          {noteData.allergies?.trim() ? noteData.allergies.trim() : "None"}
        </Text>
      </View>

      {noteData.history_of_present_illness?.trim() && (
        <View
          style={sectionStyle(
            lastSectionBeforeFinalNote === "history_of_present_illness",
          )}
          wrap={false}
        >
          <Text style={[styles.h2]}>History of present illness</Text>
          <Text>{noteData.history_of_present_illness}</Text>
        </View>
      )}

      {INTAKE_HISTORY_SECTIONS.map(({ key, title }) => {
        const content = noteData[key]?.trim();
        if (!content) {
          return null;
        }

        return (
          <View
            key={key}
            style={sectionStyle(lastSectionBeforeFinalNote === key)}
            wrap={false}
          >
            <Text style={[styles.h2]}>{title}</Text>
            <Text>{content}</Text>
          </View>
        );
      })}

      <View style={[styles.section, styles.tight]} break>
        <PdfMarkdown>{noteData.final_note}</PdfMarkdown>
      </View>
    </PdfDocument>
  );
};
